import { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { hideModal } from "actions/modal";
import { getCollections } from "selectors/mediaLibrarySelectors";
import { bulkMoveAssets } from "actions/mediaLibrary";
import { CollectionSelectorModern } from "features/media-library/components/collection-selector/collection-selector-modern";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CollectionFile } from "shared/types/files";

interface BulkMoveAssetsModalProps {
  assets: CollectionFile[];
}

const BulkMoveAssetsModal = ({ assets }: BulkMoveAssetsModalProps) => {
  const dispatch = useDispatch();
  const collections = useSelector(getCollections);
  const [selectedCollectionId, setSelectedCollectionId] = useState<
    string | null
  >(null);

  const selectedCollection =
    collections.find((collection) => collection._id === selectedCollectionId) ||
    null;

  const handleClose = useCallback(() => {
    dispatch(hideModal());
  }, [dispatch]);

  const handleMove = useCallback(() => {
    if (!selectedCollectionId) return;

    dispatch(bulkMoveAssets(assets, selectedCollectionId));
  }, [dispatch, assets, selectedCollectionId]);

  const isSingleAsset = assets.length === 1;
  const assetName = isSingleAsset ? assets[0].name || "Untitled" : null;

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-[480px] p-0" showCloseButton={false}>
        <div className="flex flex-col">
          {/* Header */}
          <DialogHeader className="px-6 py-3">
            <DialogTitle className="font-gotham text-lg font-semibold text-neutral-900">
              Move {isSingleAsset ? "Asset" : "Assets"}
            </DialogTitle>
            <DialogDescription className="font-inter text-sm text-neutral-600 mt-2">
              {isSingleAsset ? (
                <>
                  Choose a collection to move &quot;
                  <strong className="text-neutral-900">{assetName}</strong>
                  &quot; to.
                </>
              ) : (
                <>Choose a collection to move {assets.length} assets to.</>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Content */}
          <div className="px-6 pb-6 space-y-4">
            <div className="space-y-1.5">
              <label className="font-gotham text-sm font-semibold text-neutral-700">
                Collection
              </label>
              <CollectionSelectorModern
                collections={collections}
                selectedCollection={selectedCollection}
                onChange={(collectionId) => {
                  setSelectedCollectionId(
                    Array.isArray(collectionId) ? collectionId[0] : collectionId
                  );
                }}
                className="w-full"
              />
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="flex flex-row items-center justify-between sm:justify-between border-t border-neutral-200 px-6 py-3">
            <Button
              variant="ghost"
              onClick={handleClose}
              className="text-neutral-600 hover:text-neutral-700 hover:bg-neutral-100"
            >
              Cancel
            </Button>
            <Button
              variant="blue-primary"
              disabled={!selectedCollectionId}
              onClick={handleMove}
            >
              Move
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkMoveAssetsModal;
